import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { searchAPI, SimilaritySearchRequest, SearchResult, IndexStats } from '../../services/api';
import SearchResultsPanel from './SearchResultsPanel';

const SearchInterface: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [indexStats, setIndexStats] = useState<IndexStats | null>(null);
  
  // Search filters
  const [topK, setTopK] = useState(10);
  const [speciesFilter, setSpeciesFilter] = useState('');
  const [minScore, setMinScore] = useState(0.0);
  const [searchTime, setSearchTime] = useState<number | null>(null);

  const speciesOptions = [
    'dog', 'cat', 'horse', 'cow', 'sheep', 'goat', 'pig', 'bird', 'rabbit', 'ferret'
  ];

  useEffect(() => {
    loadIndexStats();
  }, []);

  const loadIndexStats = async () => {
    try {
      const stats = await searchAPI.getIndexStats();
      setIndexStats(stats);
    } catch (err) {
      console.error('Error loading index stats:', err);
    }
  };

  const performSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchRequest: SimilaritySearchRequest = {
        query_text: query,
        top_k: topK,
        min_score: minScore > 0 ? minScore : undefined,
        species_filter: speciesFilter || undefined,
      };

      const response = await searchAPI.similaritySearch(searchRequest);
      setResults(response.results);
      setSearchTime(response.search_time_ms);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTopK(10);
    setSpeciesFilter('');
    setMinScore(0.0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4, 
        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', 
        color: 'white',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
          `,
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ fontSize: '2.5rem' }}>🔍</Box>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                Case Search
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                AI-powered semantic similarity search through veterinary cases
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Index Statistics */}
      {indexStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              Database Statistics
            </Typography>
                         <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
               <Box>
                 <Typography variant="h4" color="primary">
                   {indexStats.total_vectors?.toLocaleString() || 'N/A'}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Total Cases
                 </Typography>
               </Box>
               <Box>
                 <Typography variant="h4" color="primary">
                   {indexStats.dimension || 'N/A'}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Vector Dimensions
                 </Typography>
               </Box>
               <Box>
                 <Typography variant="h4" color="primary">
                   {indexStats.index_fullness ? 
                     `${(indexStats.index_fullness * 100).toFixed(1)}%` : 'N/A'}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Index Fullness
                 </Typography>
               </Box>
             </Box>
          </CardContent>
        </Card>
      )}

      {/* Search Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search Query"
            placeholder="Describe symptoms, condition, or case details..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            multiline
            maxRows={4}
            helperText="Example: 'Dog with vomiting and lethargy' or 'Cat with respiratory issues'"
          />
        </Box>

        {/* Filters */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <FilterIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
              Search Filters
              {(topK !== 10 || speciesFilter || minScore > 0) && (
                <Chip 
                  label="Active" 
                  size="small" 
                  color="primary" 
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
               <Box sx={{ minWidth: 200 }}>
                 <Typography gutterBottom>
                   Number of Results: {topK}
                 </Typography>
                 <Slider
                   value={topK}
                   onChange={(_, value) => setTopK(value as number)}
                   min={1}
                   max={50}
                   marks={[
                     { value: 1, label: '1' },
                     { value: 10, label: '10' },
                     { value: 25, label: '25' },
                     { value: 50, label: '50' },
                   ]}
                   valueLabelDisplay="auto"
                 />
               </Box>

               <Box sx={{ minWidth: 200 }}>
                 <FormControl fullWidth>
                   <InputLabel>Species Filter</InputLabel>
                   <Select
                     value={speciesFilter}
                     label="Species Filter"
                     onChange={(e) => setSpeciesFilter(e.target.value)}
                   >
                     <MenuItem value="">
                       <em>All Species</em>
                     </MenuItem>
                     {speciesOptions.map((species) => (
                       <MenuItem key={species} value={species}>
                         {species.charAt(0).toUpperCase() + species.slice(1)}
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
               </Box>

               <Box sx={{ minWidth: 200 }}>
                 <Typography gutterBottom>
                   Min Similarity Score: {(minScore * 100).toFixed(0)}%
                 </Typography>
                 <Slider
                   value={minScore}
                   onChange={(_, value) => setMinScore(value as number)}
                   min={0}
                   max={1}
                   step={0.1}
                   marks={[
                     { value: 0, label: '0%' },
                     { value: 0.5, label: '50%' },
                     { value: 0.8, label: '80%' },
                     { value: 1, label: '100%' },
                   ]}
                   valueLabelDisplay="auto"
                   valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
                 />
               </Box>

               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Button
                   variant="outlined"
                   onClick={clearFilters}
                   startIcon={<ClearIcon />}
                   size="small"
                 >
                   Clear Filters
                 </Button>
               </Box>
             </Box>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={performSearch}
            disabled={loading || !query.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ px: 4 }}
          >
            {loading ? 'Searching...' : 'Search Cases'}
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Results Summary */}
      {!loading && results.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Typography variant="h6">
            Found {results.length} similar cases
            {searchTime && ` in ${searchTime.toFixed(2)}ms`}
          </Typography>
          {query && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              Query: "{query}"
            </Typography>
          )}
        </Paper>
      )}

      {/* No Results */}
      {!loading && results.length === 0 && query && (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="h6" gutterBottom>
            No similar cases found
          </Typography>
          <Typography variant="body2">
            Try adjusting your search query or reducing the minimum similarity score filter.
          </Typography>
        </Paper>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <SearchResultsPanel results={results} maxHeight="80vh" />
      )}
    </Box>
  );
};

export default SearchInterface; 